import React, { useState, useRef, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks.js";
import {
  Layout,
  List,
  Avatar,
  Spin,
  DatePicker,
  Collapse,
  Badge,
  Form,
  message,
  Button,
  Tag,
  Tooltip,
  Row,
  Col,
  Space,
  Typography,
} from "antd";
import {
  CalendarOutlined,
  FilterOutlined,
  MessageOutlined,
  CommentOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  RobotOutlined,
  CheckOutlined,
  CheckCircleOutlined,
  CheckCircleFilled,
  CheckCircleTwoTone,
  CheckSquareOutlined,
  CheckSquareFilled,
  CheckSquareTwoTone,
  TagOutlined,
  CloseSquareOutlined,
  DownOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import "./CustomerList.css";
import SearchInput from "../common/searchInput";
import {
  fetchCTAOptionsApi,
  fetchLanguagesListApi,
  fetchProductsListApi,
  fetchSentimentsListApi,
  fetchSociairProfilesApi,
  fetchTagsListApi,
  getCustomersDataApi,
  getFiltersApi,
} from "../../services/channels.service.js";
import { ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ChevronUp } from "lucide-react";
import { logout } from "../../store/slices/profile.slice.js";
import { useNavigate } from "react-router-dom";
import { transformString } from "../../helpers/channelNameTransformation.js";
import { debounceScroll } from "../../helpers/debounceScroll.js";
import FilterModal from "./FilterModal.jsx";
import ContactModal from "./sections/ContactModal/index.jsx";
import { checkBalance } from "../../helpers/checkBalance.js";

const { Sider } = Layout;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;
const { Text } = Typography;

const CustomerList = ({
  setCustomerInfo,
  selectedCustomerId,
  handleCustomerClick,
  setSelectedCustomerId,
  refreshCustomerList,
  isMobile,
}) => {
  const location = useLocation();
  const { phoneID } = location.state || {}; // Keep phoneID for backward compatibility
  const [searchParams, setSearchParams] = useSearchParams();

  // State to manage the collapse panel
  const [activeKey, setActiveKey] = useState([]); // Empty array means collapsed



  // Form reference for controlling form fields
  const [form] = Form.useForm();

  const [totalPages, setTotalPages] = useState();

  const scrollRef = useRef(null);
  const [showBackTopButton, setShowBackTopButton] = useState(false); //control visibility of the back to top button

  const navigate = useNavigate();
  const { state } = location;

  // Fetch All Customers
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  // Customers
  const [customers, setCustomers] = useState([]);
  const [totalChatsCount, setTotalChatsCount] = useState(0);

  // Scroll Pagination
  const [page, setPage] = useState(1);

  // Initialize state from URL parameters
  const [channel, setChannel] = useState(searchParams.get('channel') || undefined);
  const [selectedProduct, setSelectedProduct] = useState(searchParams.get('topic') || undefined); // Topic from dashboard
  const [selectedLanguage, setSelectedLanguage] = useState(searchParams.get('language') || undefined);
  const [selectedSentiment, setSelectedSentiment] = useState(searchParams.get('sentiment') || undefined);
  const [selectedCTA, setSelectedCTA] = useState(searchParams.get('cta') || undefined);
  const [selectedCTAStatus, setSelectedCTAStatus] = useState(searchParams.get('cta_status') || undefined);
  const [selectedProfiles, setSelectedProfiles] = useState(() => {
    const profileParams = searchParams
      .getAll("profile")
      .flatMap((profile) => profile.split(","))
      .filter(Boolean);
    if (profileParams.length > 0) return profileParams;

    const legacyProfileParam = searchParams.get("profiles");
    return legacyProfileParam ? legacyProfileParam.split(",").filter(Boolean) : [];
  });
  const [searchKeywords, setSearchKeywords] = useState(searchParams.get('search') || phoneID || undefined);

  const { dateRangeValue } = useAppSelector((state) => state.dateRange);

  // For date range: check URL first, then fall back to Redux state
  const urlStartDate = searchParams.get("start_date");
  const urlEndDate = searchParams.get("end_date");
  const [dates, setDates] = useState(() => {
    // If both dates exist and are not empty strings, use them
    if (urlStartDate !== null && urlEndDate !== null && urlStartDate !== "" && urlEndDate !== "") {
      return [urlStartDate, urlEndDate];
    }
    // If dates were explicitly cleared (empty strings), keep them cleared
    if (urlStartDate === "" && urlEndDate === "") {
      return [];
    }
    // Otherwise (no date params), use Redux state
    return [
      dayjs(dateRangeValue.start_date).hour(0).minute(0).second(0).format("YYYY-MM-DD HH:mm:ss"),
      dayjs(dateRangeValue.end_date).hour(23).minute(59).second(59).format("YYYY-MM-DD HH:mm:ss"),
    ];
  });

  const [dateRangeAntdValue, setDateRangeAntdValue] = useState(() => {
    // If both dates exist and are not empty strings, use them
    if (urlStartDate !== null && urlEndDate !== null && urlStartDate !== "" && urlEndDate !== "") {
      return [dayjs(urlStartDate), dayjs(urlEndDate)];
    }
    // If dates were explicitly cleared (empty strings), keep them cleared
    if (urlStartDate === "" && urlEndDate === "") {
      return null;
    }
    // Otherwise (no date params), use Redux state
    return [
      dayjs(dateRangeValue.start_date).hour(0).minute(0).second(0),
      dayjs(dateRangeValue.end_date).hour(23).minute(59).second(59),
    ];
  });

  // CTA select options
  const [ctaOptions, setCTAOptions] = useState();
  const [ctaStatus, setCTAStatus] = useState();

  const [tagsList, setTagsList] = useState();
  const [productsList, setProductsList] = useState();
  const [languagesList, setLanguagesList] = useState();
  const [sentimentsList, setSentimentsList] = useState();
  const [profilesList, setProfilesList] = useState();

  const handleCTAOptions = (value) => {
    setSelectedCTA(value);
  };

  const handleCTAStatus = (value) => {
    setSelectedCTAStatus(value);
  };

  const handleCollapseChange = (key) => {
    setActiveKey(key);
  };

  // .................................
  // Function to generate consistent colors based on names
  const getNameColor = (name) => {
    const colors = [
      "#f56a00",
      "#7265e6",
      "#ffbf00",
      "#00a2ae",
      "#1890ff",
      "#52c41a",
      "#722ed1",
      "#eb2f96",
    ];

    // Simple hash function to get consistent color for a name
    let hash = 0;
    if (!name) return colors[0];
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  // Helper function to get customer initials
  const getInitials = (name) => {
    if (!name || name === "N/A") return "?";
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  // Helper function to get channel icon based on channel name
  const getChannelIcon = (channel) => {
    if (!channel) return "mdi:message-outline"; // fallback icon

    const channelLower = channel.toLowerCase();
    const iconMap = {
      facebook: "logos:facebook",
      instagram: "skill-icons:instagram",
      whatsapp: "logos:whatsapp-icon",
      website: "streamline-plump:web-remix",
      playground: "octicon:play-16",
      tiktok: "logos:tiktok-icon",
    };

    return iconMap[channelLower] || "mdi:message-outline"; // fallback to message icon
  };

  // Helper function to get channel display name for tooltip
  const getChannelDisplayName = (channel) => {
    if (!channel) return "Unknown";
    return channel.charAt(0).toUpperCase() + channel.slice(1).toLowerCase();
  };

  const getSocialProfile = (customer) =>
    customer?.social_profile || customer?.sociar_profile || null;

  const getSocialProfileTooltip = (customer) => {
    const profile = getSocialProfile(customer);
    const channelName = getChannelDisplayName(customer?.channel);

    if (profile?.name) {
      return `${profile.name} (${channelName})`;
    }

    return channelName;
  };

  // .................................

  // Add a function to handle clearing all filters
  const handleClearFilters = () => {
    // Reset all your filter states here
    setChannel();
    setSelectedCTA();
    setSelectedCTAStatus();
    setSelectedProfiles([]);
    setDates();
    setDateRangeAntdValue();
    setSearchKeywords();
    setSelectedProduct();
    setSelectedLanguage();
    setSelectedSentiment();

    // Reset form fields to ensure UI components reflect the cleared values
    form.resetFields();

    // Display a success message to user
    message.success("All filters have been cleared");
  };

  // Determine if the panel is collapsed
  const isPanelCollapsed = !activeKey.includes("1");

  // Panel header with modified Clear All button
  const panelHeader = (
    <span>
      <FilterOutlined /> Filters
      {isPanelCollapsed ? (
        <>
          <span style={{ marginLeft: 8, fontSize: 14, color: "#607D8B" }}>
            {channel && (
              <span style={{ marginLeft: 4 }}>
                <span style={{ marginRight: "5px", color: "grey" }}>•</span>
                {channel.charAt(0).toUpperCase() + channel.slice(1)}
              </span>
            )}
            {selectedCTA && (
              <span style={{ marginLeft: 12 }}>
                <span
                  style={{
                    marginRight: "5px",
                    color: "grey",
                  }}
                >
                  •
                </span>
                {transformString(selectedCTA)}
              </span>
            )}
            {selectedProfiles?.length > 0 && (
              <span style={{ marginLeft: 12 }}>
                <span style={{ marginRight: "5px", color: "grey" }}>•</span>
                {selectedProfiles.length} profile{selectedProfiles.length > 1 ? "s" : ""}
              </span>
            )}
          </span>
        </>
      ) : (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            handleClearFilters();
          }}
          color="danger"
          variant="text"
          style={{
            marginLeft: "12px",
            paddingLeft: "8px",
            paddingRight: "8px",
            height: "24px",
          }}
          disabled={
            !channel &&
            selectedProfiles?.length === 0 &&
            !selectedCTA &&
            !selectedCTAStatus &&
            !dates &&
            !searchKeywords &&
            !selectedProduct &&
            !selectedLanguage &&
            !selectedSentiment
          }
        >
          Clear All
        </Button>
      )}
    </span>
  );

  // Detect Scroll Event
  const handleScroll = debounceScroll(() => {
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

    // Show the back to top button when user scrolls
    if (scrollTop > 200) {
      setShowBackTopButton(true);
    } else {
      setShowBackTopButton(false);
    }

    if (
      scrollTop + clientHeight + 60 >= scrollHeight &&
      !loading &&
      page <= totalPages
    ) {
      fetchAllCustomers();
    }
  }, 100);

  // Fetch All Customers
  const fetchAllCustomers = () => {
    setLoading(true);

    dispatch(
      getCustomersDataApi({
        params: {
          start_date: dates?.length > 0 ? dates[0] : null,
          end_date: dates?.length > 0 ? dates[1] : null,
          page: page,
          page_size: "10",
          search: searchKeywords,
          cta: selectedCTA,
          status: selectedCTAStatus,
          tags: channel,
          profile: selectedProfiles?.length > 0 ? selectedProfiles : undefined,
          topic: selectedProduct,
          language: selectedLanguage,
          sentiment: selectedSentiment,
        },

        finalCallback: () => {
          setLoading(false);
        },
        successCallback: (response) => {
          setCustomers((prevCustomers) => [...prevCustomers, ...response.data]);
          setTotalPages(response?.total_pages);
          setPage((prevPage) => prevPage + 1);
        },
        failureCallback: (errors) => {
          console.log("Error in getCustomersDataApi -> ", errors);
        },
      })
    );
  };

  // Fetch InitialCustomerList
  const fetchInitialCustomerList = () => {
    setLoading(true);
    setCustomers([]); // Clear existing customers first

    dispatch(
      getCustomersDataApi({
        params: {
          start_date: dates?.length > 0 ? dates[0] : null,
          end_date: dates?.length > 0 ? dates[1] : null,
          page: 1,
          page_size: "10",
          search: searchKeywords,
          cta: selectedCTA,
          status: selectedCTAStatus,
          tags: channel,
          profile: selectedProfiles?.length > 0 ? selectedProfiles : undefined,
          topic: selectedProduct,
          language: selectedLanguage,
          sentiment: selectedSentiment,
        },

        finalCallback: () => {
          setLoading(false);
        },
        successCallback: (response) => {
          setCustomers(response.data);
          setTotalPages(response?.total_pages);
          setPage(2);
          // Only auto-select first customer on desktop, not on mobile
          // This allows the back button to work properly on mobile
          if (response?.data?.length > 0 && !isMobile) {
            setSelectedCustomerId(response?.data?.[0]?.customer_id);
            setCustomerInfo(response?.data?.[0]);
          }
          setTotalChatsCount(response?.total_records || 0);
        },
        failureCallback: (errors) => {
          console.log("Error in getCustomersDataApi -> ", errors);
        },
      })
    );
  };

  const handleChannelChange = (value) => {
    setChannel(value);
  };

  const handleProfilesChange = (value) => {
    setSelectedProfiles(value || []);
  };

  const fetchCTAOptions = () => {
    dispatch(
      fetchCTAOptionsApi({
        finalCallback: () => { },
        successCallback: (response) => {
          const ctaList = response?.types?.map((item) => {
            return { label: transformString(item?.name), value: item?.name };
          });
          const ctaStatus = response?.status?.map((item) => {
            return { label: transformString(item?.name), value: item?.name };
          });

          setCTAOptions(ctaList);
          setCTAStatus(ctaStatus);
        },
        failureCallback: (errors) => { },
      })
    );
  };

  const fetchFilters = () => {
    dispatch(
      getFiltersApi({
        params: { filter_route: "ai_response" },
        finalCallback: () => { },
        successCallback: (response) => {
          const list = response?.channel?.map((item) => {
            return { label: item, value: item };
          });
          setTagsList(list);
        },
        failureCallback: (errors) => { },
      })
    );
  };

  const fetchSociairProfiles = () => {
    dispatch(
      fetchSociairProfilesApi({
        finalCallback: () => { },
        successCallback: (response) => {
          const list = response?.profiles?.map((item) => {
            const labelName = item?.name || item?.username || item?.raw_id || "Unnamed profile";

            return {
              label: labelName,
              value: item?.raw_id,
              name: labelName,
              photo: item?.photo,
              profileType: item?.profile_type,
            };
          }).filter((item) => item?.value);

          setProfilesList(list || []);
        },
        failureCallback: (errors) => { },
      })
    );
  };

  const fetchProductsList = () => {
    dispatch(
      fetchProductsListApi({
        finalCallback: () => { },
        successCallback: (response) => {
          const list = response?.map((item) => {
            return { label: item?.topic, value: item?.topic };
          });
          setProductsList(list);
        },
        failureCallback: (errors) => { },
      })
    );
  };

  const fetchLanguagesList = () => {
    dispatch(
      fetchLanguagesListApi({
        finalCallback: () => { },
        successCallback: (response) => {
          const list = response?.map((item) => {
            return { label: item, value: item };
          });
          setLanguagesList(list);
        },
        failureCallback: (errors) => { },
      })
    );
  };

  const fetchSentimentsList = () => {
    dispatch(
      fetchSentimentsListApi({
        finalCallback: () => { },
        successCallback: (response) => {
          const list = response?.map((item) => {
            return { label: item, value: item };
          });
          setSentimentsList(list);
        },
        failureCallback: (errors) => { },
      })
    );
  };

  // Handle Date range filter
  const handleDateChange = (values) => {
    if (values && values.length === 2) {
      // Keep original Day.js objects for the picker
      setDateRangeAntdValue(values);
      // Send time-inclusive strings to the API
      const stringDatesWithTime = values.map((date) =>
        date.format("YYYY-MM-DD HH:mm:ss")
      );
      setDates(stringDatesWithTime);
    } else {
      setDateRangeAntdValue(null);
      setDates();
    }
  };

  useEffect(() => {
    if (refreshCustomerList) {
      fetchInitialCustomerList();
    }
  }, [refreshCustomerList]);

  // UseEffects
  useEffect(() => {
    // Fetch
    fetchInitialCustomerList();
  }, [
    dates,
    searchKeywords,
    selectedCTA,
    selectedCTAStatus,
    channel,
    selectedProfiles,
    selectedProduct,
    selectedLanguage,
    selectedSentiment,
  ]);

  useEffect(() => {
    fetchCTAOptions();
    fetchFilters();
    fetchProductsList();
    fetchLanguagesList();
    fetchSentimentsList();
    fetchSociairProfiles();
  }, []);

  // Update URL parameters when filter state changes
  useEffect(() => {
    const params = new URLSearchParams();

    if (searchKeywords) params.set("search", searchKeywords);
    if (channel) params.set("channel", channel);
    selectedProfiles?.forEach((profile) => params.append("profile", profile));
    if (selectedCTA) params.set("cta", selectedCTA);
    if (selectedCTAStatus) params.set("cta_status", selectedCTAStatus);
    if (selectedProduct) params.set("topic", selectedProduct);
    if (selectedLanguage) params.set("language", selectedLanguage);
    if (selectedSentiment) params.set("sentiment", selectedSentiment);

    // Handle date range: set params if dates exist, set empty if explicitly cleared
    if (dates && dates.length === 2) {
      params.set("start_date", dates[0]);
      params.set("end_date", dates[1]);
    } else if (dates && dates.length === 0) {
      // Dates were explicitly cleared - track this with empty values
      params.set("start_date", "");
      params.set("end_date", "");
    }

    const newSearch = params.toString();
    const currentSearch = searchParams.toString();

    if (newSearch !== currentSearch) {
      setSearchParams(params, { replace: true });
    }
  }, [searchKeywords, channel, selectedProfiles, selectedCTA, selectedCTAStatus, selectedProduct, selectedLanguage, selectedSentiment, dates, setSearchParams, searchParams]);

  // Scroll to top
  const scrollToTop = () => {
    scrollRef.current.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const defaultDates =
    dateRangeValue?.start_date && dateRangeValue?.end_date
      ? [
          dayjs(dateRangeValue.start_date)
            .hour(0)
            .minute(0)
            .second(0)
            .format("YYYY-MM-DD HH:mm:ss"),
          dayjs(dateRangeValue.end_date)
            .hour(23)
            .minute(59)
            .second(59)
            .format("YYYY-MM-DD HH:mm:ss"),
        ]
      : null;

  const isDateFilterActive =
    Array.isArray(dates) &&
    dates.length === 2 &&
    (!defaultDates || dates[0] !== defaultDates[0] || dates[1] !== defaultDates[1]);

  const hasActiveFilters =
    Boolean(searchKeywords && searchKeywords.trim()) ||
    Boolean(channel) ||
    Boolean(selectedCTA) ||
    Boolean(selectedCTAStatus) ||
    Boolean(selectedProduct) ||
    Boolean(selectedLanguage) ||
    Boolean(selectedSentiment) ||
    isDateFilterActive;

  const emptyConversationsText = hasActiveFilters
    ? "No conversations found for the selected filters."
    : "No conversations found.";

  const emptyConversationsContent = (
    <div
      style={{
        height: "100%",
        minHeight: "200px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        textAlign: "center",
        color: "#8c8c8c",
      }}
    >
      <UnorderedListOutlined style={{ fontSize: "48px", color: "#bfbfbf" }} />
      <div style={{ fontSize: "16px", fontWeight: 500 }}>
        {emptyConversationsText}
      </div>
    </div>
  );

  return (
    <div style={{ width: isMobile ? "100%" : "auto", height: "100%" }}>
      <Sider
        width={isMobile ? "100%" : 400}
        theme="light"
        className="customer-list-sider"
        style={{
          borderRadius: "0",
          height: "100%",
          borderRight: isMobile ? "none" : "1px solid #f0f0f0",
        }}
      >
        {/* Sticky Header Section - Filter and Search */}
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 10px",
              // borderBottom: "1px solid #f0f0f0",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "2px",
                margin: "6px",
              }}
            >
              <FilterModal
                ctaOptions={ctaOptions}
                ctaStatus={ctaStatus}
                tagsList={tagsList}
                productsList={productsList}
                languagesList={languagesList}
                sentimentsList={sentimentsList}
                profilesList={profilesList}
                selectedCTA={selectedCTA}
                selectedCTAStatus={selectedCTAStatus}
                channel={channel}
                selectedProfiles={selectedProfiles}
                selectedProduct={selectedProduct}
                selectedLanguage={selectedLanguage}
                selectedSentiment={selectedSentiment}
                dateRangeAntdValue={dateRangeAntdValue}
                handleCTAOptions={handleCTAOptions}
                handleCTAStatus={handleCTAStatus}
                handleChannelChange={handleChannelChange}
                handleProfilesChange={handleProfilesChange}
                handleDateChange={handleDateChange}
                setSelectedProduct={setSelectedProduct}
                setSelectedLanguage={setSelectedLanguage}
                setSelectedSentiment={setSelectedSentiment}
                setSearchKeywords={setSearchKeywords}
              />
              <span
                type="default"
                size="middle"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  marginLeft: 16
                }}
              >
                <span
                  style={{
                    fontWeight: "bold",
                    backgroundColor: "#f0f0f0",
                    padding: "2px 4px",
                    borderRadius: "4px",
                    marginRight: "4px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "24px",
                    minHeight: "24px",
                  }}
                >
                  {loading && !totalChatsCount ? (
                    <iconify-icon
                      icon="codex:loader"
                      width="18"
                      height="18"
                      style={{ display: "block" }}
                    ></iconify-icon>
                  ) : (
                    <span>{totalChatsCount}</span>
                  )}
                </span>
                <span>Conversations</span>
              </span>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <ContactModal />
            </div>
          </div>

          <div
            style={{
              paddingLeft: "16px",
              paddingRight: "16px",
              paddingBottom: "10px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <SearchInput
              setSearchKeywords={setSearchKeywords}
              searchKeywords={searchKeywords}
            />
            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchInitialCustomerList();
              }}
              disabled={loading}
            />
          </div>
        </div>

        {/* Scrollable Conversation List */}
        <div style={{ flex: 1, overflow: "hidden", padding: "0 0px", minHeight: 0, paddingBottom: "0px" }}>
          <Spin spinning={loading} wrapperClassName="customer-list-spin" style={{ height: "100%" }}>
            <div style={{ height: "100%", overflowY: "auto" }} ref={scrollRef} onScroll={handleScroll}>
              <List
                itemLayout="horizontal"
                dataSource={customers}
                split={false}
                locale={{ emptyText: emptyConversationsContent }}
                style={{
                  // margin: "0 16px",
                }}
                renderItem={(item) => {
                  const socialProfile = getSocialProfile(item);

                  return (
                    <List.Item
                      key={item.id}
                      onClick={() => {
                        setSelectedCustomerId(item?.customer_id);
                        setCustomerInfo(item);
                        return handleCustomerClick(item?.customer_id);
                      }}
                      className={`customer-list-item ${selectedCustomerId === item.customer_id ? "selected" : ""
                        }`}
                    >
                      <List.Item.Meta
                        style={{
                          marginRight: "8px",
                          wordBreak: "break-word",
                        }}
                        avatar={
                          <div style={{ position: "relative" }}>
                            <Badge count={item?.unreadCount} offset={[-5, 5]}>
                              <Avatar
                                size={44}
                                style={{
                                  backgroundColor: getNameColor(item?.customer_name),
                                  fontSize: "16px",
                                }}
                              >
                                {getInitials(item?.customer_name)}
                              </Avatar>
                            </Badge>
                            {/* Channel Icon Badge */}
                            <div
                              style={{
                                position: "absolute",
                                bottom: -4,
                                right: -6,
                                width: 22,
                                height: 22,
                                borderRadius: "50%",
                                backgroundColor: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                              }}
                            >
                              <Tooltip title={getSocialProfileTooltip(item)}>
                                <iconify-icon
                                  icon={getChannelIcon(item?.channel)}
                                  width="22"
                                  height="22"
                                  style={{ display: "block" }}
                                ></iconify-icon>
                              </Tooltip>
                            </div>
                          </div>
                        }
                        title={
                          <div className="customer-title">
                            <div className="customer-title-content">
                              <span
                                style={{
                                  fontWeight:
                                    selectedCustomerId === item.customer_id
                                      ? "600"
                                      : "500",
                                }}
                              >
                                {item?.customer_name ?? "N/A"}
                              </span>
                              {socialProfile?.name && (
                                <Tooltip title={getSocialProfileTooltip(item)}>
                                  <div className="customer-social-profile">
                                    <Avatar
                                      size={16}
                                      src={socialProfile?.profile_image}
                                      style={{
                                        backgroundColor: getNameColor(socialProfile?.name),
                                        fontSize: "9px",
                                        flex: "0 0 auto",
                                      }}
                                    >
                                      {getInitials(socialProfile?.name)}
                                    </Avatar>
                                    <span className="customer-social-profile-name">
                                      {socialProfile.name}
                                    </span>
                                  </div>
                                </Tooltip>
                              )}
                            </div>
                          </div>
                        }
                      description={
                        <>
                          <div
                            style={{
                              display: "flex",
                            }}
                          >
                            <div
                              className="message-preview"
                              style={{
                                display: "-webkit-box",
                                webkitBoxOrient: "vertical",
                                WebkitLineClamp: 2,
                                lineHeight: "1.5",
                                maxHeight: "calc(1.5em * 2)",
                              }}
                            >
                              {item?.latest_chat_content?.role === "user" ||
                                item?.latest_chat_content?.role === "agent" ? (
                                <UserOutlined
                                  className="message-icon"
                                  style={{ marginRight: "6px", color: "#69c0ff" }}
                                />
                              ) : (
                                item?.latest_chat_content?.role === "assistant" && (
                                  <RobotOutlined
                                    className="message-icon"
                                    style={{
                                      marginRight: "6px",
                                      color: "#69c0ff",
                                    }}
                                  />
                                )
                              )}

                              {item?.latest_chat_content?.content === "" ? (
                                <p
                                  style={{
                                    fontStyle: "italic",
                                    display: "inline",
                                    color: "#999999",
                                  }}
                                >
                                  Sent an Image
                                </p>
                              ) : (
                                item?.latest_chat_content?.content ??
                                "No messages found"
                              )}
                            </div>
                            {!item?.ai_replied && (
                              <div>
                                <Tooltip title="No Reply from AI">
                                  <ExclamationCircleOutlined
                                    style={{
                                      paddingLeft: "8px",
                                      fontSize: "16px",
                                      color: "#faad14",
                                    }}
                                  />
                                </Tooltip>
                              </div>
                            )}
                          </div>

                          {item?.cta_distinct_count?.length > 0 && (
                            <div
                              style={{
                                marginTop: "8px",
                                display: "flex",
                                gap: "4px",
                              }}
                            >
                              {item?.cta_distinct_count
                                ?.slice(0, 2)
                                .map((ctaItem, index) => (
                                  <Button
                                    style={{
                                      padding: "1.5px 6px",
                                      height: "24px",
                                      fontSize: "12px",
                                      borderRadius: "4px",
                                      color: "#1890ff",
                                    }}
                                    key={index}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // navigate("/cta");
                                      const params = new URLSearchParams({
                                        type: ctaItem?.type,
                                        status: ctaItem?.unresolved_count === 0 ? "resolved" : "open",
                                        search: item.email ?? item?.customer_id,
                                      });
                                      navigate(`/cta?${params.toString()}`);
                                    }}
                                  >
                                    {transformString(ctaItem?.type)}
                                    {ctaItem?.unresolved_count > 1 ? (
                                      <Badge
                                        count={ctaItem?.unresolved_count}
                                        size="small"
                                        style={{
                                          backgroundColor: "#40a9ff",
                                          marginLeft: "2px",
                                          fontSize: "10px",
                                          height: "16px",
                                          lineHeight: "16px",
                                          minWidth: "16px",
                                        }}
                                      />
                                    ) : (
                                      ctaItem?.unresolved_count === 0 && (
                                        <CheckCircleOutlined
                                          style={{
                                            fontSize: "14px",
                                            marginLeft: "2px",
                                          }}
                                        />
                                      )
                                    )}
                                  </Button>
                                ))}

                              {item?.cta_distinct_count?.length > 2 && (
                                <Tooltip
                                  color="#fafafa"
                                  title={
                                    <div>
                                      {item?.cta_distinct_count
                                        ?.slice(2)
                                        .map((ctaItem, index) => (
                                          <Tag
                                            style={{
                                              height: "24px",
                                              marginBottom: "8px",
                                            }}
                                          >
                                            {transformString(ctaItem?.type)}
                                          </Tag>
                                        ))}
                                    </div>
                                  }
                                >
                                  <Tag
                                    style={{
                                      paddingTop: "1px",
                                      paddingBottom: "1px",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  >
                                    +{item?.cta_distinct_count?.length - 2}
                                  </Tag>
                                </Tooltip>
                              )}
                            </div>
                          )}

                          {item?.latest_chat_content?.created_at && (
                            <div
                              style={{
                                textAlign: "end",
                                fontSize: "12px",
                                marginTop: "8px",
                                color: "#b8b8b8",
                              }}
                            >
                              {(() => {
                                const messageDate = dayjs(
                                  item?.latest_chat_content?.created_at
                                );
                                const today = dayjs();

                                // If message is from today, show time only (e.g., "4:13 PM")
                                if (
                                  messageDate.format("YYYY-MM-DD") ===
                                  today.format("YYYY-MM-DD")
                                ) {
                                  return messageDate.format("h:mm A");
                                }

                                // If message is from this week (last 7 days), show day name (e.g., "Sun")
                                if (messageDate.isAfter(today.subtract(7, "day"))) {
                                  return messageDate.format("ddd");
                                }

                                // Otherwise show month and day (e.g., "Dec 10")
                                return messageDate.format("MMM D");
                              })()}
                            </div>
                          )}
                        </>
                      }
                      />
                    </List.Item>
                  );
                }}
              />
              {showBackTopButton && (
                <Button
                  type="primary"
                  shape="circle"
                  icon={<ChevronUp size={18} />}
                  onClick={() => {
                    scrollToTop();
                    setTimeout(() => {
                      fetchInitialCustomerList();
                    }, 600);
                  }}
                  className="back-to-top-button"
                />
              )}
            </div>
          </Spin>
        </div>
      </Sider >
    </div >
  );
};

export default CustomerList;
